import {
  extractErrorMessage,
  personCapabilitiesSelector,
  PersonCapabilityEntity,
  personSelector,
  rolesSelector,
  TeamMembershipEntity,
  teamMembershipsSelector,
  useGetPersonCapabilitiesQuery,
  useGetPersonQuery,
  useGetRolesByPersonQuery,
  useGetTeamMembershipsByPersonQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useCallback, useMemo} from "react";
import {useChangeOwnPassword} from "../../account/use-change-own-password";
import {useAuth} from "../../app/AuthProvider";
import {OtsBrand, OtsColorSchemePreference} from "../../theme/OtsTheme";
import {toDisplayText} from "../../utils/localized-text";
import {UserProfile} from "./UserProfile";
import {UserProfileCapability, UserProfileTeam} from "./user-profile-utils";

/**
 * Who holds the password of someone signed in through single sign-on. Deliberately generic: the
 * organization's sign-in may be any provider, and naming one would be wrong for the others.
 */
export const ssoPasswordManagedBy = "your organization's sign-in";

export type UserProfilePageProps = {
  /** Wins over the app's brand. */
  brand?: OtsBrand;
  /** Wins over the app's scheme. */
  colorScheme?: OtsColorSchemePreference;
  /** Called with the person's id after they change their password. */
  onPasswordChanged?: (personId: string) => void;
  /** Shows a Sign out action when given. */
  onSignOut?: () => void;
  notificationSettingsHref?: string;
  onManageNotifications?: () => void;
};

const toProfileTeam = (membership: TeamMembershipEntity): UserProfileTeam => ({
  id: membership.teamId,
  name: membership.teamName,
  description: membership.teamDescription,
  scopeName: membership.scopeObjectName,
  parentTeamName: membership.parentTeamName,
  // Switched off either way -- the membership or the whole team -- reads the same to the person.
  enabled: membership.enabled !== false && membership.teamEnabled !== false,
  effectiveStartDttm: membership.effectiveStartDttm,
  effectiveEndDttm: membership.effectiveEndDttm
});

const toProfileCapability = (capability: PersonCapabilityEntity): UserProfileCapability => ({
  id: capability.id,
  // The code is a fallback for one added on the server before this client knows its label.
  name: toDisplayText(capability.capability?.displayValue) ?? capability.capability?.code ?? "Unknown",
  primary: capability.primary
});

const teamsErrorMessage = (error: unknown): string =>
  // A server from before GET /teams/memberships existed.
  typeof error === "object" && error !== null && "status" in error && error.status === 404
    ? "Team memberships aren't available on this server yet."
    : extractErrorMessage(error, "Your teams couldn't be loaded.");

/**
 * The signed-in person's profile, loaded for the shared view. Any app inside `AuthProvider` can
 * route to it; it brands itself from the app unless given a brand.
 *
 * Someone signed in through single sign-on is told their password is managed there, rather than
 * offered a change this account cannot make.
 */
export const UserProfilePage = ({
  brand,
  colorScheme,
  onPasswordChanged,
  onSignOut,
  notificationSettingsHref,
  onManageNotifications
}: UserProfilePageProps) => {
  const {userId, ssoAuthenticated} = useAuth();

  const selectPerson = useMemo(() => personSelector(), []);
  const {person, isPersonLoading, personError, refetch: refetchPerson} = useGetPersonQuery(userId ?? skipToken, {
    selectFromResult: (result) => ({
      person: selectPerson(result),
      isPersonLoading: result.isLoading,
      personError: result.error
    })
  });

  const selectRoles = useMemo(() => rolesSelector(), []);
  const {roles, isRolesLoading} = useGetRolesByPersonQuery(person?.id ?? skipToken, {
    selectFromResult: (result) => ({
      roles: selectRoles(result),
      isRolesLoading: result.isLoading
    })
  });

  const selectTeamMemberships = useMemo(() => teamMembershipsSelector(), []);
  const {memberships, isTeamsLoading, teamsError} = useGetTeamMembershipsByPersonQuery(person?.id ?? skipToken, {
    selectFromResult: (result) => ({
      // Undefined until loaded, so "no teams" is never shown for teams that are still on their way.
      memberships: result.isSuccess ? selectTeamMemberships(result) : undefined,
      isTeamsLoading: result.isLoading,
      teamsError: result.error
    })
  });

  const teams = useMemo(() => memberships?.map(toProfileTeam), [memberships]);

  const selectCapabilities = useMemo(() => personCapabilitiesSelector(), []);
  const {personCapabilities, isCapabilitiesLoading, capabilitiesError} = useGetPersonCapabilitiesQuery(person?.id ?? skipToken, {
    selectFromResult: (result) => ({
      // Undefined until loaded, so "none assigned" is never shown for capabilities still on their way.
      personCapabilities: result.isSuccess ? selectCapabilities(result) : undefined,
      isCapabilitiesLoading: result.isLoading,
      capabilitiesError: result.error
    })
  });

  const capabilities = useMemo(() => personCapabilities?.map(toProfileCapability), [personCapabilities]);

  const handlePasswordChanged = useCallback(() => {
    if (person) {
      onPasswordChanged?.(person.id);
    }
  }, [person, onPasswordChanged]);

  const changePassword = useChangeOwnPassword(person?.id, handlePasswordChanged);

  return (
    <UserProfile
      brand={brand}
      colorScheme={colorScheme}
      person={person}
      roles={roles}
      teams={teams}
      teamsLoading={isTeamsLoading}
      teamsError={teamsError ? teamsErrorMessage(teamsError) : undefined}
      capabilities={capabilities}
      capabilitiesLoading={isCapabilitiesLoading}
      capabilitiesError={capabilitiesError ? extractErrorMessage(capabilitiesError, "Your capabilities couldn't be loaded.") : undefined}
      loading={isPersonLoading || (Boolean(person) && isRolesLoading)}
      error={personError ? extractErrorMessage(personError, "Something went wrong loading your profile.") : undefined}
      onRetry={refetchPerson}
      onChangePassword={changePassword}
      passwordManagedBy={ssoAuthenticated ? ssoPasswordManagedBy : undefined}
      onSignOut={onSignOut}
      notificationSettingsHref={notificationSettingsHref}
      onManageNotifications={onManageNotifications}/>
  );
};
